import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Divider,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'react-toastify';
import api from '@/modules/vendor/service/interceptor';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthInfo } from '@/redux/auth/auth.slice';

const TwoFactorAuth = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth.auth);
  
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [setupStep, setSetupStep] = useState(0);
  const [secretKey, setSecretKey] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [disableVerificationCode, setDisableVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [isPasswordVerifying, setIsPasswordVerifying] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [disableErrorMessage, setDisableErrorMessage] = useState('');
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');

  // Clear error when verification code changes
  const handleVerificationCodeChange = (event) => {
    setVerificationCode(event.target.value);
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  // Clear error when disable verification code changes
  const handleDisableVerificationCodeChange = (event) => {
    setDisableVerificationCode(event.target.value);
    if (disableErrorMessage) {
      setDisableErrorMessage('');
    }
  };

  // Clear error when password changes
  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    if (passwordErrorMessage) {
      setPasswordErrorMessage('');
    }
  };

  // Check current 2FA status
  useEffect(() => {
    check2FAStatus();
  }, []);

  const check2FAStatus = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/vendors/2fa/status');
      setIsEnabled(response.data.data.is_enabled);
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      toast.error('Failed to check 2FA status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle2FA = async () => {
    if (isEnabled) {
      // Show disable dialog
      setShowDisableDialog(true);
    } else {
      // Enable 2FA - show setup dialog
      setShowSetupDialog(true);
      setSetupStep(0);
    }
  };

  const handleDisable2FA = async () => {
    if (!disableVerificationCode.trim()) {
      setDisableErrorMessage('Please enter the verification code');
      return;
    }

    if (disableVerificationCode.length !== 6) {
      setDisableErrorMessage('Please enter a valid 6-digit code');
      return;
    }

    try {
      setIsLoading(true);
      setDisableErrorMessage(''); // Clear any previous error
      await api.post('/vendors/2fa/disable', { 
        verification_code: disableVerificationCode 
      });
      setIsEnabled(false);
      
      // Update Redux store
      dispatch(setAuthInfo({ ...auth, two_factor_enabled: false }));
      
      setShowDisableDialog(false);
      setDisableVerificationCode('');
      toast.success('Two-Factor Authentication disabled successfully');
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      const errorMsg = error.response?.data?.message || 'Failed to disable 2FA';
      setDisableErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableWithPassword = async () => {
    if (!password.trim()) {
      setPasswordErrorMessage('Please enter your password');
      return;
    }

    try {
      setIsPasswordVerifying(true);
      setPasswordErrorMessage(''); // Clear any previous error
      await api.post('/vendors/2fa/disable-with-password', { 
        password: password 
      });
      setIsEnabled(false);
      
      // Update Redux store
      dispatch(setAuthInfo({ ...auth, two_factor_enabled: false }));
      
      setShowPasswordDialog(false);
      setPassword('');
      toast.success('Two-Factor Authentication disabled successfully');
    } catch (error) {
      console.error('Error disabling 2FA with password:', error);
      const errorMsg = error.response?.data?.message || 'Failed to disable 2FA. Please check your password.';
      setPasswordErrorMessage(errorMsg);
    } finally {
      setIsPasswordVerifying(false);
    }
  };

  const generateSecretKeyHandler = async () => {
    try {
      setIsLoading(true);
      const response = await api.post('/vendors/2fa/generate-secret');
      setSecretKey(response.data.data.secret);
      setQrCodeUrl(response.data.data.qr_code_url);
      setSetupStep(1);
    } catch (error) {
      console.error('Error generating secret key:', error);
      toast.error('Failed to generate secret key');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyAndEnable2FA = async () => {
    if (!verificationCode.trim()) {
      setErrorMessage('Please enter the verification code');
      return;
    }

    if (verificationCode.length !== 6) {
      setErrorMessage('Please enter a valid 6-digit code');
      return;
    }

    try {
      setIsVerifying(true);
      setErrorMessage(''); // Clear any previous error
      
      await api.post('/vendors/2fa/enable', { 
        verification_code: verificationCode 
      });
      
      setIsEnabled(true);
      
      // Update Redux store
      dispatch(setAuthInfo({ ...auth, two_factor_enabled: true }));
      
      setShowSetupDialog(false);
      setVerificationCode('');
      toast.success('Two-Factor Authentication enabled successfully');
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      const errorMsg = error.response?.data?.message || 'Failed to verify 2FA code';
      setErrorMessage(errorMsg);
    } finally {
      setIsVerifying(false);
    }
  };

  const steps = [
    {
      label: 'Generate Secret Key',
      description: 'Generate a unique secret key for your account',
    },
    {
      label: 'Scan QR Code',
      description: 'Scan the QR code with your authenticator app',
    },
    {
      label: 'Verify Setup',
      description: 'Enter the verification code to complete setup',
    },
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Two-Factor Authentication
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Google Authenticator
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add an extra layer of security to your account using Google Authenticator
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={handleToggle2FA}
                  disabled={isLoading}
                />
              }
              label={isEnabled ? 'Enabled' : 'Disabled'}
            />
          </Box>

          {isEnabled && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Two-Factor Authentication is currently enabled for your account.
            </Alert>
          )}

          {!isEnabled && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Two-Factor Authentication is currently disabled. Enable it to add an extra layer of security.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      <Dialog
        open={showSetupDialog}
        onClose={() => setShowSetupDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Stepper activeStep={setupStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {step.description}
                  </Typography>

                  {index === 0 && (
                    <Box>
                      <Button
                        variant="contained"
                        onClick={generateSecretKeyHandler}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : null}
                      >
                        Generate Secret Key
                      </Button>
                    </Box>
                  )}

                  {index === 1 && secretKey && (
                    <Box>
                      <Paper sx={{ p: 2, mb: 2, textAlign: 'center' }}>
                        <QRCodeSVG value={qrCodeUrl} size={200} />
                      </Paper>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Secret Key (backup):
                      </Typography>
                      <TextField
                        value={secretKey}
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputProps={{ readOnly: true }}
                        sx={{ mb: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Scan this QR code with Google Authenticator or any compatible authenticator app.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => setSetupStep(2)}
                        sx={{ mt: 2 }}
                      >
                        Next: Verify Setup
                      </Button>
                    </Box>
                  )}

                  {index === 2 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        Enter the 6-digit code from your authenticator app:
                      </Typography>
                      <TextField
                        label="Verification Code"
                        value={verificationCode}
                        onChange={handleVerificationCodeChange}
                        placeholder="123456"
                        inputProps={{ maxLength: 6 }}
                        sx={{ mb: 2 }}
                      />
                      {errorMessage && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {errorMessage}
                        </Alert>
                      )}
                      <Box>
                        <Button
                          variant="contained"
                          onClick={verifyAndEnable2FA}
                          disabled={isVerifying || verificationCode.length !== 6}
                          startIcon={isVerifying ? <CircularProgress size={20} /> : null}
                          sx={{ mr: 1 }}
                        >
                          {isVerifying ? 'Verifying...' : 'Verify & Enable'}
                        </Button>
                        <Button
                          onClick={() => setSetupStep(1)}
                          disabled={isVerifying}
                        >
                          Back
                        </Button>
                      </Box>
                    </Box>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSetupDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog
        open={showDisableDialog}
        onClose={() => setShowDisableDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            To disable Two-Factor Authentication, please enter the 6-digit code from your authenticator app:
          </Typography>
          <TextField
            label="Verification Code"
            value={disableVerificationCode}
            onChange={handleDisableVerificationCodeChange}
            placeholder="123456"
            inputProps={{ maxLength: 6 }}
            fullWidth
            sx={{ mb: 2 }}
          />
          {disableErrorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {disableErrorMessage}
            </Alert>
          )}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                setShowDisableDialog(false);
                setShowPasswordDialog(true);
              }}
              sx={{ textTransform: 'none' }}
            >
              Try other way
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisableDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDisable2FA}
            disabled={isLoading || disableVerificationCode.length !== 6}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Disabling...' : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Verification Dialog */}
      <Dialog
        open={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            To disable Two-Factor Authentication using your password, please enter your current password:
          </Typography>
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={handlePasswordChange}
            placeholder="Enter your password"
            fullWidth
            sx={{ mb: 2 }}
          />
          {passwordErrorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordErrorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDisableWithPassword}
            disabled={isPasswordVerifying || !password.trim()}
            startIcon={isPasswordVerifying ? <CircularProgress size={20} /> : null}
          >
            {isPasswordVerifying ? 'Disabling...' : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TwoFactorAuth;
